import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Users as UsersIcon, Plus, Search, Shield, Key, UserX, UserCheck, Trash2, Loader2, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useAuth } from "../lib/auth";

const ROLE_LABELS = {
  owner:          "Owner",
  manager:        "Manager",
  salesman:       "Salesman",
  recovery_agent: "Recovery Agent",
  cashier:        "Cashier",
};
const NON_OWNER_ROLES = ["manager", "salesman", "recovery_agent", "cashier"];

const PERM_SCHEMA = {
  customers:    ["view", "create", "edit", "delete"],
  loans:        ["view", "create", "edit", "delete"],
  payments:     ["view", "add", "edit", "delete"],
  reports:      ["view"],
  recovery:     ["view", "manage"],
  settings:     ["view", "edit"],
  subscription: ["view", "manage"],
  referral:     ["view", "withdraw"],
  users:        ["view", "manage"],
};

function CreateDialog({ open, onOpenChange, remaining, onDone }) {
  const [form, setForm] = useState({ full_name: "", email: "", mobile: "", password: "", role: "manager" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("Employee added");
      onOpenChange(false);
      setForm({ full_name: "", email: "", mobile: "", password: "", role: "manager" });
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Employee</DialogTitle>
          <DialogDescription>
            {remaining === null ? "Unlimited slots" : `${remaining} slot${remaining === 1 ? "" : "s"} remaining on your plan.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Full name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="u-c-name" /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="u-c-role"><SelectValue /></SelectTrigger>
                <SelectContent>{NON_OWNER_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="u-c-email" /></div>
            <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="u-c-mobile" /></div>
            <div className="col-span-2"><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="u-c-password" /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="u-c-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add employee"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PermissionsDialog({ open, onOpenChange, target, onDone }) {
  const [perms, setPerms] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) setPerms(JSON.parse(JSON.stringify(target.permissions || {}))); }, [target]);
  const toggle = (mod, act) => {
    setPerms((p) => {
      const next = { ...p, [mod]: { ...(p[mod] || {}) } };
      next[mod][act] = !next[mod][act];
      return next;
    });
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${target.id}`, { permissions: perms });
      toast.success("Permissions saved");
      onOpenChange(false); onDone && onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  if (!target) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Permissions — {target.full_name || target.owner_name}</DialogTitle>
          <DialogDescription>Role default: <b>{ROLE_LABELS[target.role]}</b>. Override any checkbox below.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto" data-testid="u-perm-grid">
          {Object.entries(PERM_SCHEMA).map(([mod, acts]) => (
            <div key={mod} className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="font-display font-bold text-sm text-slate-700 capitalize">{mod}</div>
              <div className="mt-2 space-y-1.5">
                {acts.map((a) => (
                  <label key={a} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={!!perms?.[mod]?.[a]} onChange={() => toggle(mod, a)} data-testid={`u-perm-${mod}-${a}`} />
                    <span className="capitalize">{a}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="u-perm-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save permissions"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ShopUsers() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [cap, setCap] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [permTarget, setPermTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setRows(data.items || []);
      setTotal(data.total || 0);
      setCap(data.cap);
      setRemaining(data.remaining_slots);
    } catch (err) { toast.error(err?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (uid, status) => {
    try {
      await api.patch(`/users/${uid}`, { status });
      toast.success(status === "active" ? "Activated" : "Deactivated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
  };
  const resetPw = async (uid, name) => {
    const pw = window.prompt(`New password for ${name} (min 6 chars):`);
    if (!pw) return;
    if (pw.length < 6) { toast.error("Min 6 chars"); return; }
    try {
      await api.post(`/users/${uid}/reset-password`, { new_password: pw });
      toast.success("Password reset");
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
  };
  const deleteRow = async (uid, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("Employee deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };
  const changeRole = async (uid, newRole) => {
    try {
      await api.patch(`/users/${uid}`, { role: newRole });
      toast.success("Role updated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Role update failed"); }
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.full_name || r.owner_name || "").toLowerCase().includes(s)
        || (r.email || "").toLowerCase().includes(s)
        || (r.role || "").toLowerCase().includes(s);
  });
  const isCapReached = cap !== null && total >= cap;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="shop-users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2.5">
            <UsersIcon className="w-7 h-7 text-emerald-600" /> Users
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            Aap ki team ke employees. Har user ke apne login credentials aur permissions honge.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9" data-testid="u-search" />
          </div>
          <Button onClick={() => setCreateOpen(true)} disabled={isCapReached} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="u-add-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Employee
          </Button>
        </div>
      </div>

      {/* Plan cap bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-md border border-slate-200 p-4"><div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Total</div><div className="mt-1 font-display text-2xl font-bold" data-testid="u-count-total">{total}</div></div>
        <div className="rounded-md border border-emerald-100 bg-emerald-50 p-4"><div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">Cap</div><div className="mt-1 font-display text-2xl font-bold text-emerald-700" data-testid="u-count-cap">{cap === null ? "∞" : cap}</div></div>
        <div className="rounded-md border border-sky-100 bg-sky-50 p-4"><div className="text-[11px] uppercase tracking-widest font-bold text-sky-700">Remaining</div><div className="mt-1 font-display text-2xl font-bold text-sky-700" data-testid="u-count-remaining">{remaining === null ? "∞" : remaining}</div></div>
        <div className="rounded-md border border-slate-200 p-4"><div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Active</div><div className="mt-1 font-display text-2xl font-bold" data-testid="u-count-active">{rows.filter((r) => (r.status ?? "active") === "active").length}</div></div>
      </div>

      {isCapReached && (
        <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 flex items-center gap-2 text-sm" data-testid="u-cap-warning">
          <AlertCircle className="w-4 h-4 shrink-0" />
          Aap ka plan limit ({cap} users) puri ho gayi hai. Aur employees add karne ke liye plan upgrade karein.
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500 tracking-widest">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th>Role</th>
              <th>Email</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={5} className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-slate-400" /></td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-500">No employees yet.</td></tr>}
            {filtered.map((r) => {
              const isSelf = r.id === me?.id;
              const isOwner = r.role === "owner";
              const status = r.status ?? "active";
              return (
                <tr key={r.id} data-testid={`u-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.full_name || r.owner_name}</div>
                    {r.mobile && <div className="text-xs text-slate-500">{r.mobile}</div>}
                  </td>
                  <td>
                    {isSelf || isOwner ? (
                      <span className="text-xs font-bold rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">{ROLE_LABELS[r.role] || r.role}</span>
                    ) : (
                      <Select value={r.role} onValueChange={(v) => changeRole(r.id, v)}>
                        <SelectTrigger className="h-8 w-36" data-testid={`u-role-${r.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{NON_OWNER_ROLES.map((rr) => <SelectItem key={rr} value={rr}>{ROLE_LABELS[rr]}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="text-xs">{r.email}</td>
                  <td>
                    {status === "active" ? (
                      <span className="text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">Active</span>
                    ) : (
                      <span className="text-xs font-bold text-red-700 bg-red-50 rounded-full px-2 py-0.5">Inactive</span>
                    )}
                  </td>
                  <td className="pr-4 py-2 space-x-1">
                    <button onClick={() => setPermTarget(r)} title="Permissions" className="text-slate-600 hover:text-emerald-700 p-1.5 rounded hover:bg-slate-100" data-testid={`u-perms-${r.id}`}><Shield className="w-4 h-4" /></button>
                    {!isSelf && <button onClick={() => resetPw(r.id, r.full_name || r.owner_name)} title="Reset password" className="text-slate-600 hover:text-slate-900 p-1.5 rounded hover:bg-slate-100" data-testid={`u-resetpw-${r.id}`}><Key className="w-4 h-4" /></button>}
                    {!isSelf && !isOwner && (status === "active" ? (
                      <button onClick={() => setStatus(r.id, "inactive")} title="Deactivate" className="text-red-600 hover:text-red-800 p-1.5 rounded hover:bg-red-50" data-testid={`u-deactivate-${r.id}`}><UserX className="w-4 h-4" /></button>
                    ) : (
                      <button onClick={() => setStatus(r.id, "active")} title="Activate" className="text-emerald-600 hover:text-emerald-800 p-1.5 rounded hover:bg-emerald-50" data-testid={`u-activate-${r.id}`}><UserCheck className="w-4 h-4" /></button>
                    ))}
                    {!isSelf && !isOwner && (
                      <button onClick={() => deleteRow(r.id, r.full_name || r.owner_name)} title="Delete" className="text-red-600 hover:text-red-800 p-1.5 rounded hover:bg-red-50" data-testid={`u-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} remaining={remaining} onDone={load} />
      <PermissionsDialog open={!!permTarget} onOpenChange={(o) => !o && setPermTarget(null)} target={permTarget} onDone={load} />
    </div>
  );
}
